<template>
  <!-- Recurring invoices: recurring billing profiles (e.g. Starlink) + the
       pending-review queue of generated invoices awaiting release. -->
  <v-container fluid>
    <div class="d-flex align-center mb-3">
      <h2>Recurring Invoices</h2>
      <v-spacer></v-spacer>
      <v-btn
        color="secondary"
        class="mr-2"
        outlined
        :loading="generating"
        @click="generateNow"
      >
        <v-icon class="mr-1">mdi-cog-refresh</v-icon>Generate Now
      </v-btn>
      <v-btn color="primary" @click="openCreate">
        <v-icon class="mr-1">mdi-plus</v-icon>New Profile
      </v-btn>
    </div>

    <v-tabs v-model="tab" class="mb-3">
      <v-tab>Profiles</v-tab>
      <v-tab>
        Pending Review
        <v-chip v-if="pending.length" x-small color="warning" class="ml-2">{{
          pending.length
        }}</v-chip>
      </v-tab>
      <v-tab>Deleted Profiles</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <!-- ============ PROFILES ============ -->
      <v-tab-item>
        <v-card outlined>
          <v-card-text>
            <div class="d-flex align-center mb-2">
              <v-select
                v-model="statusFilter"
                :items="statusFilterOptions"
                label="Status"
                dense
                hide-details
                style="max-width: 200px"
                class="mr-3"
              ></v-select>
              <v-text-field
                v-model="search"
                label="Search"
                prepend-icon="mdi-magnify"
                dense
                hide-details
                style="max-width: 320px"
              ></v-text-field>
              <v-spacer></v-spacer>
              <v-btn
                color="secondary"
                class="mr-2"
                outlined
                :loading="generating"
                @click="generateNow"
              >
                <v-icon class="mr-1">mdi-cog-refresh</v-icon>Generate Invoices
              </v-btn>
              <v-btn color="primary" class="mr-2" @click="openCreate">
                <v-icon class="mr-1">mdi-plus</v-icon>New Profile
              </v-btn>
              <v-btn text color="success" @click="refresh" :loading="loading">
                <v-icon class="mr-1">mdi-refresh</v-icon>Refresh
              </v-btn>
            </div>

            <v-data-table
              dense
              :loading="loading"
              :items="filteredProfiles"
              :headers="profileHeaders"
              :search="search"
            >
              <template #[`item.amount`]="{ item }">
                ₱{{ formatAmount(item.amount) }}
              </template>
              <template #[`item.next_due_date`]="{ item }">
                {{ item.next_due_date || "—" }}
              </template>
              <template #[`item.status`]="{ item }">
                <v-chip x-small :color="statusColor(item.status)" dark>
                  {{ item.status }}
                </v-chip>
              </template>
              <template #[`item.actions`]="{ item }">
                <v-icon small class="mr-2" @click="openEdit(item)"
                  >mdi-pencil</v-icon
                >
                <v-icon small class="mr-2" @click="openHistory(item)"
                  >mdi-history</v-icon
                >
                <v-icon
                  v-if="item.status === 'active'"
                  small
                  color="orange"
                  @click="pauseProfile(item)"
                  title="Pause"
                  >mdi-pause-circle</v-icon
                >
                <v-icon
                  v-else-if="item.status === 'paused'"
                  small
                  color="green"
                  @click="resumeProfile(item)"
                  title="Resume"
                  >mdi-play-circle</v-icon
                >
                <v-icon
                  small
                  color="error"
                  class="ml-2"
                  @click="deleteProfile(item)"
                  title="Delete"
                  >mdi-delete</v-icon
                >
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- ============ PENDING REVIEW ============ -->
      <v-tab-item>
        <v-card outlined>
          <v-card-text>
            <v-alert v-if="pending.length" type="info" dense text>
              {{ pending.length }} generated invoice{{
                pending.length === 1 ? "" : "s"
              }}
              awaiting review. Releasing an invoice sends the customer's
              invoice-created SMS.
            </v-alert>
            <div class="d-flex align-center mb-2">
              <v-spacer></v-spacer>
              <v-btn
                text
                color="success"
                @click="refreshPending"
                :loading="pendingLoading"
              >
                <v-icon class="mr-1">mdi-refresh</v-icon>Refresh
              </v-btn>
            </div>
            <v-data-table
              dense
              :loading="pendingLoading"
              :items="pending"
              :headers="pendingHeaders"
            >
              <template #[`item.total_amount`]="{ item }">
                ₱{{ formatAmount(item.total_amount) }}
              </template>
              <template #[`item.profile`]="{ item }">
                {{
                  item.recurring_profile ? item.recurring_profile.label : "—"
                }}
              </template>
              <template #[`item.actions`]="{ item }">
                <v-btn
                  x-small
                  color="success"
                  class="mr-2"
                  :loading="releasingId === item.id"
                  @click="releaseInvoice(item)"
                >
                  Release
                </v-btn>
                <v-btn
                  x-small
                  color="error"
                  outlined
                  :loading="discardingId === item.id"
                  @click="discardPendingInvoice(item)"
                >
                  Delete
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- ============ DELETED PROFILES ============ -->
      <v-tab-item>
        <v-card outlined>
          <v-card-text>
            <div class="d-flex align-center mb-2">
              <v-spacer></v-spacer>
              <v-btn
                text
                color="success"
                @click="refreshDeleted"
                :loading="deletedLoading"
              >
                <v-icon class="mr-1">mdi-refresh</v-icon>Refresh
              </v-btn>
            </div>
            <v-data-table
              dense
              :loading="deletedLoading"
              :items="deleted_profiles"
              :headers="deletedHeaders"
            >
              <template #[`item.amount`]="{ item }">
                ₱{{ formatAmount(item.amount) }}
              </template>
              <template #[`item.deleted_at`]="{ item }">
                {{
                  item.deleted_at ? String(item.deleted_at).slice(0, 10) : "—"
                }}
              </template>
              <template #[`item.actions`]="{ item }">
                <v-btn
                  x-small
                  color="primary"
                  :loading="restoringId === item.id"
                  @click="restoreProfile(item)"
                >
                  Restore
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <!-- ============ CREATE / EDIT DIALOG ============ -->
    <v-dialog v-model="dialog" max-width="800" persistent scrollable>
      <v-card>
        <v-card-title>
          {{ editing ? "Edit" : "New" }} Recurring Profile
        </v-card-title>
        <v-card-text>
          <v-form ref="form">
            <v-row dense>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="form.customer_id"
                  :items="customers"
                  item-text="customer_name"
                  item-value="id"
                  label="Customer *"
                  :rules="[(v) => !!v || 'Required']"
                  dense
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.branch_id"
                  :items="branches"
                  :item-text="branchText"
                  item-value="id"
                  label="Branch"
                  dense
                ></v-select>
              </v-col>
              <v-col cols="6" md="3">
                <v-text-field
                  v-model.number="form.due_day_of_month"
                  label="Due day of month"
                  type="number"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="6" md="3">
                <v-text-field
                  v-model.number="form.reminder_days_before"
                  label="Remind days before"
                  type="number"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="6" md="3">
                <v-text-field
                  v-model="form.starts_on"
                  label="Starts on *"
                  type="date"
                  :rules="[(v) => !!v || 'Required']"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="6" md="3">
                <v-text-field
                  v-model="form.ends_on"
                  label="Ends on (optional)"
                  type="date"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.status"
                  :items="['active', 'paused', 'ended']"
                  label="Status"
                  dense
                ></v-select>
              </v-col>
            </v-row>

            <div class="d-flex align-center mt-3 mb-1">
              <strong>Lines</strong>
              <v-spacer></v-spacer>
              <v-btn x-small text color="primary" @click="addLine">
                <v-icon x-small class="mr-1">mdi-plus</v-icon>Add line
              </v-btn>
            </div>
            <v-row
              v-for="(line, i) in form.lines"
              :key="i"
              dense
              align="center"
            >
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="line.description"
                  label="Description *"
                  dense
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="6" md="3">
                <v-select
                  v-model="line.service_id"
                  :items="serviceOptions"
                  item-text="service_name"
                  item-value="id"
                  label="Service (optional)"
                  clearable
                  dense
                  hide-details
                  @change="onServicePicked(line, $event)"
                ></v-select>
              </v-col>
              <v-col cols="3" md="2">
                <v-text-field
                  v-model.number="line.quantity"
                  label="Qty"
                  type="number"
                  dense
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="3" md="2">
                <v-text-field
                  v-model.number="line.unit_price"
                  label="Unit ₱"
                  type="number"
                  dense
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="1">
                <v-icon small color="error" @click="removeLine(i)"
                  >mdi-delete</v-icon
                >
              </v-col>
            </v-row>
            <div class="text-right mt-2 font-weight-bold">
              Lines total: ₱{{ formatAmount(linesTotal) }}
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false">Cancel</v-btn>
          <v-btn color="primary" :loading="saving" @click="save">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============ HISTORY DIALOG ============ -->
    <v-dialog v-model="historyDialog" max-width="720" scrollable>
      <v-card>
        <v-card-title>
          Generation History
          <span v-if="historyProfile" class="ml-2 grey--text">
            — {{ historyProfile.label }}
          </span>
        </v-card-title>
        <v-card-text>
          <v-data-table
            dense
            :items="historyRuns"
            :headers="historyHeaders"
            :items-per-page="10"
          >
            <template #[`item.status`]="{ item }">
              <v-chip
                x-small
                :color="item.status === 'success' ? 'success' : 'error'"
                dark
                >{{ item.status }}</v-chip
              >
            </template>
            <template #[`item.invoice_id`]="{ item }">
              {{
                item.invoice
                  ? item.invoice.invoice_code
                  : item.invoice_id || "—"
              }}
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="historyDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="caption grey--text text-right mt-2">RecurringInvoices.vue</div>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "RecurringInvoices",
  data() {
    return {
      tab: 0,
      loading: false,
      pendingLoading: false,
      deletedLoading: false,
      saving: false,
      generating: false,
      releasingId: null,
      restoringId: null,
      discardingId: null,
      search: "",
      statusFilter: "all",
      statusFilterOptions: [
        { text: "All", value: "all" },
        { text: "Active", value: "active" },
        { text: "Paused", value: "paused" },
        { text: "Ended", value: "ended" },
      ],
      dialog: false,
      editing: false,
      editingId: null,
      form: this.emptyForm(),
      historyDialog: false,
      historyProfile: null,
      historyRuns: [],
      profileHeaders: [
        { text: "Customer", value: "customer.customer_name" },
        { text: "Label", value: "label" },
        { text: "Amount", value: "amount" },
        { text: "Next Due", value: "next_due_date" },
        { text: "Status", value: "status" },
        { text: "Lines", value: "lines_count" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      pendingHeaders: [
        { text: "Invoice #", value: "invoice_code" },
        { text: "Customer", value: "customer.customer_name" },
        { text: "Amount", value: "total_amount" },
        { text: "Profile", value: "profile" },
        { text: "Invoice Date", value: "invoice_date" },
        { text: "Due Date", value: "due_date" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      historyHeaders: [
        { text: "Period", value: "period" },
        { text: "Status", value: "status" },
        { text: "Invoice", value: "invoice_id" },
        { text: "Generated", value: "generated_at" },
        { text: "Error", value: "error" },
      ],
      deletedHeaders: [
        { text: "Customer", value: "customer.customer_name" },
        { text: "Label", value: "label" },
        { text: "Amount", value: "amount" },
        { text: "Deleted", value: "deleted_at" },
        { text: "Lines", value: "lines_count" },
        { text: "Actions", value: "actions", sortable: false },
      ],
    };
  },
  computed: {
    ...mapGetters({
      profiles: "recurring_invoice/profiles",
      deleted_profiles: "recurring_invoice/deleted_profiles",
      pending: "recurring_invoice/pending",
      customers: "customer/customers",
      branches: "branch/branches",
      services: "service/services",
    }),
    serviceOptions() {
      return this.services || [];
    },
    filteredProfiles() {
      if (this.statusFilter === "all") return this.profiles;
      return this.profiles.filter((p) => p.status === this.statusFilter);
    },
    linesTotal() {
      return (this.form.lines || []).reduce(
        (sum, l) =>
          sum + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0),
        0
      );
    },
  },
  async created() {
    await Promise.all([
      this.refresh(),
      this.refreshPending(),
      this.refreshDeleted(),
      this.customers.length ? null : this.get_customers(),
      this.branches.length ? null : this.get_branches(),
      this.services && this.services.length ? null : this.get_services(),
    ]);
  },
  methods: {
    ...mapActions({
      get_profiles: "recurring_invoice/get_profiles",
      create_profile: "recurring_invoice/create_profile",
      update_profile: "recurring_invoice/update_profile",
      pause_profile: "recurring_invoice/pause_profile",
      resume_profile: "recurring_invoice/resume_profile",
      delete_profile: "recurring_invoice/delete_profile",
      get_deleted_profiles: "recurring_invoice/get_deleted_profiles",
      restore_profile: "recurring_invoice/restore_profile",
      get_pending: "recurring_invoice/get_pending",
      generate_now: "recurring_invoice/generate_now",
      discard_pending: "recurring_invoice/discard_pending",
      release_invoice: "recurring_invoice/release_invoice",
      get_profile: "recurring_invoice/get_profile",
      get_customers: "customer/get_customers",
      get_branches: "branch/get_branches",
      get_services: "service/get_services",
    }),
    emptyForm() {
      return {
        customer_id: null,
        branch_id: null,
        label: "",
        amount: 0,
        due_day_of_month: 2,
        reminder_days_before: 5,
        starts_on: new Date().toISOString().slice(0, 10),
        ends_on: null,
        status: "active",
        lines: [
          { description: "", service_id: null, quantity: 1, unit_price: 0 },
        ],
      };
    },
    branchText(b) {
      return b.branch_name || b.name || `Branch ${b.id}`;
    },
    formatAmount(v) {
      return Number(v || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    statusColor(status) {
      return status === "active"
        ? "green"
        : status === "paused"
        ? "orange"
        : "grey";
    },
    async refresh() {
      this.loading = true;
      try {
        await this.get_profiles();
      } finally {
        this.loading = false;
      }
    },
    async generateNow() {
      const ok = await this.$confirm({
        title: "Generate recurring invoices?",
        message:
          "Generate pending-review invoices for all due recurring profiles now. Profiles already billed for this period are skipped — nothing is duplicated.",
        color: "primary",
        icon: "mdi-cog-refresh",
      });
      if (!ok) return;
      this.generating = true;
      try {
        const res = await this.generate_now();
        const parts = [`${res.generated} generated`];
        if (res.skipped) parts.push(`${res.skipped} skipped`);
        if (res.failed) parts.push(`${res.failed} failed`);
        this.$alert({
          title: "Generation complete",
          message: `${res.due_profiles} due profile${
            res.due_profiles === 1 ? "" : "s"
          }: ${parts.join(", ")}.${
            res.generated ? " Review them in the Pending Review tab." : ""
          }`,
          color: res.failed ? "warning" : "success",
          icon: res.failed ? "mdi-alert" : "mdi-check-circle",
        });
        if (res.generated) this.tab = 1; // jump to Pending Review
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e.response ? e.response.data.message : e.message,
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.generating = false;
      }
    },
    async refreshPending() {
      this.pendingLoading = true;
      try {
        await this.get_pending();
      } finally {
        this.pendingLoading = false;
      }
    },
    openCreate() {
      this.editing = false;
      this.editingId = null;
      this.form = this.emptyForm();
      this.dialog = true;
    },
    openEdit(item) {
      this.editing = true;
      this.editingId = item.id;
      this.form = {
        customer_id: item.customer_id,
        branch_id: item.branch_id,
        label: item.label,
        amount: item.amount,
        due_day_of_month: item.due_day_of_month,
        reminder_days_before: item.reminder_days_before,
        starts_on: this.toDateInput(item.starts_on),
        ends_on: this.toDateInput(item.ends_on),
        status: item.status,
        lines: (item.lines || []).map((l) => ({
          description: l.description,
          service_id: l.service_id,
          quantity: l.quantity,
          unit_price: l.unit_price,
        })),
      };
      if (!this.form.lines.length) this.addLine();
      this.dialog = true;
    },
    toDateInput(v) {
      if (!v) return null;
      // Handles both "YYYY-MM-DD" and ISO datetime strings.
      return String(v).slice(0, 10);
    },
    onServicePicked(line, serviceId) {
      // Picking a service fills the line's unit price and sets the description to
      // the service name (overwriting any leftover text) so the invoice line/remark
      // always matches the chosen service. The field stays editable afterward.
      const svc = (this.services || []).find((s) => s.id === serviceId);
      if (!svc) return;
      line.unit_price = Math.round(Number(svc.service_amount) || 0);
      line.description = svc.service_name;
    },
    addLine() {
      this.form.lines.push({
        description: "",
        service_id: null,
        quantity: 1,
        unit_price: 0,
      });
    },
    removeLine(i) {
      this.form.lines.splice(i, 1);
    },
    async save() {
      if (this.$refs.form && !this.$refs.form.validate()) return;
      const cleanLines = (this.form.lines || []).filter(
        (l) => l.description && l.description.trim()
      );
      if (!cleanLines.length) {
        this.$alert({
          title: "No lines",
          message: "Add at least one line with a description.",
          color: "error",
          icon: "mdi-alert-circle",
        });
        return;
      }
      // Label + amount are derived, not entered: the label is the first line's
      // description, the amount is the lines total (the actual invoice charge).
      const derivedLabel = cleanLines[0].description.trim();
      const payload = {
        ...this.form,
        label: derivedLabel,
        amount: this.linesTotal,
        ends_on: this.form.ends_on || null,
        lines: cleanLines.map((l) => ({
          description: l.description,
          service_id: l.service_id || null,
          quantity: Number(l.quantity) || 1,
          unit_price: Number(l.unit_price) || 0,
        })),
      };
      this.saving = true;
      try {
        if (this.editing) {
          await this.update_profile({ id: this.editingId, payload });
        } else {
          await this.create_profile(payload);
        }
        this.dialog = false;
        this.$store.dispatch("notification/set_notification", {
          message: "Recurring profile saved",
          color: "success",
        });
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e.response ? e.response.data.message : e.message,
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.saving = false;
      }
    },
    async pauseProfile(item) {
      await this.pause_profile(item.id);
    },
    async resumeProfile(item) {
      await this.resume_profile(item.id);
    },
    async refreshDeleted() {
      this.deletedLoading = true;
      try {
        await this.get_deleted_profiles();
      } finally {
        this.deletedLoading = false;
      }
    },
    async deleteProfile(item) {
      const ok = await this.$confirm({
        title: "Delete profile?",
        message: `Delete the recurring profile "${item.label}" for ${
          item.customer ? item.customer.customer_name : ""
        }? It moves to Deleted Profiles and can be restored; its history is kept.`,
        color: "error",
        icon: "mdi-delete",
      });
      if (!ok) return;
      try {
        await this.delete_profile(item.id);
        await this.refreshDeleted();
        this.$store.dispatch("notification/set_notification", {
          message: "Profile deleted",
          color: "success",
        });
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e.response ? e.response.data.message : e.message,
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
    async restoreProfile(item) {
      this.restoringId = item.id;
      try {
        await this.restore_profile(item.id);
        this.$store.dispatch("notification/set_notification", {
          message: "Profile restored",
          color: "success",
        });
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e.response ? e.response.data.message : e.message,
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.restoringId = null;
      }
    },
    async openHistory(item) {
      this.historyProfile = item;
      this.historyRuns = [];
      this.historyDialog = true;
      const detail = await this.get_profile(item.id);
      this.historyRuns = detail.runs || [];
    },
    async releaseInvoice(item) {
      const ok = await this.$confirm({
        title: "Release invoice?",
        message: `Release ${item.invoice_code} and send the customer's invoice SMS?`,
        color: "success",
        icon: "mdi-send",
      });
      if (!ok) return;
      this.releasingId = item.id;
      try {
        await this.release_invoice(item.id);
        this.$store.dispatch("notification/set_notification", {
          message: "Invoice released",
          color: "success",
        });
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e.response ? e.response.data.message : e.message,
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.releasingId = null;
      }
    },
    async discardPendingInvoice(item) {
      const ok = await this.$confirm({
        title: "Delete pending invoice?",
        message: `Delete the pending-review invoice ${
          item.invoice_code
        } for ${
          item.customer ? item.customer.customer_name : ""
        }? It was never released, so its draft journal is removed and any stock/serials are restored. This cannot be undone.`,
        color: "error",
        icon: "mdi-delete",
      });
      if (!ok) return;
      this.discardingId = item.id;
      try {
        await this.discard_pending(item.id);
        this.$store.dispatch("notification/set_notification", {
          message: "Pending invoice deleted",
          color: "success",
        });
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e.response ? e.response.data.message : e.message,
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.discardingId = null;
      }
    },
  },
};
</script>
